import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authService } from '../lib/api';
import type { User, LoginRequest } from '../types/api';
import { marcarEntrada } from '../features/equipe/sessao';

interface AuthState {
  // Estado
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Ações
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
  clearAuth: () => void;
  
  // Utilitários
  getAuthHeaders: () => { Authorization?: string };
  isTokenExpired: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Estado inicial
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      // Ação de login
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true });
        
        try {
          const response = await authService.login(credentials);
          marcarEntrada('cliente');
          
          set({
            user: response,
            token: response.token,
            isAuthenticated: true,
            isLoading: false
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Ação de logout
      logout: () => {
        get().clearAuth();
      },

      // Definir usuário (usado quando já temos os dados)
      setUser: (user: User) => {
        set({
          user,
          token: user.token,
          isAuthenticated: true
        });
      },

      // Limpar autenticação
      clearAuth: () => {
        // Chaves do fluxo antigo (AuthContext), que ninguém mais escreve.
        // A remoção fica por um tempo para limpar sessões já existentes.
        localStorage.removeItem('user');
        localStorage.removeItem('token');

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        });
      },

      // Obter headers de autenticação
      getAuthHeaders: () => {
        const { token } = get();
        return token ? { Authorization: `Bearer ${token}` } : {};
      },

      // Verificar se token expirou
      isTokenExpired: () => {
        const { user } = get();
        
        if (!user?.expiresAt) return true;
        
        const expirationTime = new Date(user.expiresAt).getTime();
        const currentTime = new Date().getTime();
        
        return currentTime >= expirationTime;
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      
      // Função para verificar se deve persistir
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      }),
      
      // Função executada quando o store é hidratado
      onRehydrateStorage: () => (state) => {
        if (state?.isTokenExpired()) {
          state.clearAuth();
        }
      }
    }
  )
);

// Selectors para facilitar o uso
export const useAuth = () => {
  const store = useAuthStore();
  
  return {
    user: store.user,
    token: store.token,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    login: store.login,
    logout: store.logout,
    setUser: store.setUser,
    getAuthHeaders: store.getAuthHeaders,
    isTokenExpired: store.isTokenExpired
  };
};

export const useAuthUser = () => useAuthStore((state) => state.user);
export const useAuthToken = () => useAuthStore((state) => state.token);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
// Acesso à sessão fora de componentes React, como nos interceptors do axios.
// Existe para que nada além deste store leia a sessão do localStorage.
export const getAuthToken = (): string | null => {
  const state = useAuthStore.getState();
  if (!state.token || state.isTokenExpired()) return null;
  return state.token;
};

export const clearAuthSession = () => useAuthStore.getState().clearAuth();
